import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Package, ShoppingCart } from "lucide-react";
import { fmtTRY } from "@/lib/format";

/**
 * Tedarikçi Siparişi Sepeti dialogu.
 * Olumlu teklifteki tüm items[] bir sepet gibi listelenir. Kullanıcı her kalem için
 * ayrı tedarikçi + adet + fiyat + teslim tarihi belirleyip toplu sipariş açabilir.
 *
 * Backward compat: quote.items boşsa tek-satırlı klasik akışa düşer (eski teklifler).
 *
 * Props:
 *   quote:  PriceQuote — kaynak teklif
 *   onClose(): void
 *   onCreated(orders[]): void   — bir veya birden fazla sipariş açıldıktan sonra
 */
export default function SupplierOrderDialog({ quote, onClose, onCreated }) {
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  const preferredSupplierName = String(quote?.supplier_name || "").trim();
  const suppliers = useMemo(
    () => contacts.filter((c) => c.type === "supplier" || c.type === "both"),
    [contacts]
  );
  const autoMatchId = useMemo(() => {
    if (!preferredSupplierName) return "";
    const norm = (s) => String(s || "").toLocaleLowerCase("tr").trim();
    const m = suppliers.find((c) => norm(c.name) === norm(preferredSupplierName));
    return m ? m.id : "";
  }, [suppliers, preferredSupplierName]);

  // Sepet: quote.items varsa her item bir satır; yoksa tek satır (top-level fields).
  const initialRows = useMemo(() => {
    const rawItems = Array.isArray(quote?.items) && quote.items.length > 0
      ? quote.items
      : [{
          title: quote?.title || null,
          box_type: quote?.box_type || "abox",
          length_mm: quote?.length_mm, width_mm: quote?.width_mm, height_mm: quote?.height_mm,
          sheet_width_mm: quote?.sheet_width_mm, sheet_length_mm: quote?.sheet_length_mm,
          flute: quote?.flute, quality: quote?.quality, unit: quote?.unit,
          quantity: quote?.quantity || 1,
          sheet_m2: quote?.sheet_m2, total_m2: quote?.total_m2,
          stock_id: quote?.stock_id, stock_sku: quote?.stock_sku, stock_name: quote?.stock_name,
          unit_cost: quote?.unit_cost || 0,
          karton_total: quote?.karton_total || 0,
        }];
    return rawItems.map((it, i) => {
      const qty = Number(it.quantity) || 1;
      // Tedarikçi siparişinde MALİYET (marj öncesi) esas alınır — Fiyat Hesaplama'nın
      // kaydettiği `cost_unit` (₺/m² tedarikçi maliyeti) ve `cost_total` (toplam maliyet).
      // SALES tarafı (unit_cost, karton_total) MALİYET yerine geçemez → onları kullanma;
      // eski quotes'ta cost bilgisi yoksa 0 gösterip kullanıcıdan elle girmesini iste.
      let unitCostVal = 0;
      let totalCostVal = 0;
      if (it.cost_unit != null && it.cost_unit !== 0) unitCostVal = it.cost_unit;
      else if (it.cost_total != null && it.total_m2) unitCostVal = it.cost_total / it.total_m2;
      if (it.cost_total != null && it.cost_total !== 0) totalCostVal = it.cost_total;
      else if (unitCostVal > 0 && it.total_m2) totalCostVal = unitCostVal * it.total_m2;
      return {
        _uid: `${i}-${Math.random().toString(36).slice(2, 6)}`,
        include: true,
        supplier_contact_id: "",     // otomatik dolacak (autoMatch)
        quantity: qty,
        unit_cost: unitCostVal,
        total_cost: totalCostVal,
        cost_missing: unitCostVal === 0,   // UI uyarısı için: eski kayıt, TED maliyet yok
        delivery_date: "",
        notes: "",
        // Snapshot alanları
        title: it.title || null,
        box_type: it.box_type || "abox",
        length_mm: it.length_mm, width_mm: it.width_mm, height_mm: it.height_mm,
        sheet_width_mm: it.sheet_width_mm, sheet_length_mm: it.sheet_length_mm,
        flute: it.flute, quality: it.quality, unit: it.unit,
        sheet_m2: it.sheet_m2, total_m2: it.total_m2,
        stock_id: it.stock_id, stock_sku: it.stock_sku, stock_name: it.stock_name,
      };
    });
  }, [quote]);

  const [rows, setRows] = useState(initialRows);
  const [defaultDate, setDefaultDate] = useState(quote?.date || new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
  }, []);

  // Otomatik eşleşen tedarikçiyi (varsa) tüm satırların supplier alanına yaz — bir kez.
  useEffect(() => {
    if (!autoMatchId) return;
    setRows((prev) => prev.map((r) => r.supplier_contact_id ? r : { ...r, supplier_contact_id: autoMatchId }));
  }, [autoMatchId]); // eslint-disable-line

  const updateRow = (uid, patch) =>
    setRows((prev) => prev.map((r) => (r._uid === uid ? { ...r, ...patch } : r)));
  const setAllSuppliersTo = (cid) =>
    setRows((prev) => prev.map((r) => ({ ...r, supplier_contact_id: cid })));

  const activeRows = rows.filter((r) => r.include);
  const totalAmount = activeRows.reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const supplierCount = new Set(activeRows.map((r) => r.supplier_contact_id).filter(Boolean)).size;
  const anyMissingSupplier = activeRows.some((r) => !r.supplier_contact_id);

  const save = async () => {
    if (activeRows.length === 0) { toast.error("En az bir kalem seçilmeli"); return; }
    if (anyMissingSupplier) { toast.error("Her kalem için tedarikçi seçin"); return; }
    setSaving(true);
    const created = [];
    try {
      for (const r of activeRows) {
        const { data } = await api.post("/supplier-orders", {
          quote_id: quote.id,
          supplier_contact_id: r.supplier_contact_id,
          date: defaultDate,
          delivery_date: r.delivery_date || null,
          quantity: Number(r.quantity) || 1,
          unit_cost: Number(r.unit_cost) || 0,
          total_cost: Number(r.total_cost) || 0,
          notes: (r.notes || "").trim() || null,
          // Ürün snapshot — teklif üstü fields'ı override ediyor (her kalem farklı olabilir)
          box_type: r.box_type,
          flute: r.flute,
          quality: r.quality,
          unit: r.unit,
          sheet_m2: r.sheet_m2 || null,
          total_m2: r.total_m2 || null,
          stock_id: r.stock_id || null,
          stock_sku: r.stock_sku || null,
          stock_name: r.stock_name || (r.title || null),
        });
        created.push(data);
      }
      toast.success(`${created.length} sipariş açıldı${supplierCount > 1 ? ` (${supplierCount} tedarikçi)` : ""}`);
      onCreated && onCreated(created);
    } catch (e) {
      toast.error(`Sipariş açılırken hata (${created.length}/${activeRows.length} oluşturuldu): ` + (e?.response?.data?.detail || e?.message));
      // En az bir başarı varsa listeyi yenile
      if (created.length > 0) onCreated && onCreated(created);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="supplier-order-dialog-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="supplier-order-dialog"
        className="bg-white border-2 border-black w-full max-w-5xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-black">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <ShoppingCart size={16} /> Sipariş Sepeti — Teklifi Tedarikçilere Dağıt
          </h3>
          <button onClick={onClose} data-testid="supplier-order-close" className="p-1"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Toplu ayarlar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-neutral-50 border border-black/10 p-3">
            <div>
              <div className="label-caps mb-1">Sipariş Tarihi</div>
              <input type="date" className="input" value={defaultDate}
                     data-testid="supplier-order-date"
                     onChange={(e) => setDefaultDate(e.target.value)} />
            </div>
            <div>
              <div className="label-caps mb-1">Tümünü Şu Tedarikçiye Ata</div>
              <select
                data-testid="supplier-bulk-select"
                onChange={(e) => setAllSuppliersTo(e.target.value)}
                className="input bg-white"
                defaultValue=""
              >
                <option value="">— seç —</option>
                {suppliers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="text-xs text-neutral-500">
              <div className="label-caps mb-1">Özet</div>
              <div className="font-mono-num text-neutral-700">
                {activeRows.length} kalem · {supplierCount} tedarikçi · <b>{fmtTRY(totalAmount)}</b>
              </div>
              {preferredSupplierName && (
                <div className="text-[10px] mt-1">Öneri: <b>{preferredSupplierName}</b>{autoMatchId ? " (otomatik atandı)" : " · cari bulunamadı"}</div>
              )}
            </div>
          </div>

          {/* Kalem tablosu */}
          <div className="border border-black/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-neutral-100 border-b border-black/10">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="text-left px-2 py-2 font-normal text-neutral-500">Ürün</th>
                  <th className="text-left px-2 py-2 font-normal text-neutral-500">Tedarikçi *</th>
                  <th className="text-right px-2 py-2 font-normal text-neutral-500 w-20">Adet</th>
                  <th className="text-right px-2 py-2 font-normal text-neutral-500 w-24">Birim (₺/m²)</th>
                  <th className="text-right px-2 py-2 font-normal text-neutral-500 w-28">Toplam (₺)</th>
                  <th className="text-left px-2 py-2 font-normal text-neutral-500 w-32">Teslim</th>
                  <th className="text-left px-2 py-2 font-normal text-neutral-500">Not</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r._uid} className={`border-b border-black/5 align-top ${r.include ? "" : "opacity-40"}`}
                      data-testid={`order-cart-row-${i}`}>
                    <td className="px-2 py-2 text-center">
                      <input type="checkbox" checked={r.include}
                             data-testid={`order-cart-include-${i}`}
                             onChange={(e) => updateRow(r._uid, { include: e.target.checked })}
                             className="w-3.5 h-3.5 accent-black" />
                    </td>
                    <td className="px-2 py-2 min-w-[180px]">
                      <div className="font-semibold">{r.flute || "—"} · {r.quality || "—"}</div>
                      <div className="text-[10px] text-neutral-500">
                        {r.box_type === "plaka"
                          ? `Düz Plaka · ${r.sheet_width_mm}×${r.sheet_length_mm} mm`
                          : `A Box · ${r.length_mm}×${r.width_mm}×${r.height_mm} mm`}
                      </div>
                      {r.title && <div className="text-[10px] text-neutral-500 italic">"{r.title}"</div>}
                      {r.stock_sku && <div className="text-[10px] text-neutral-400">{r.stock_sku}</div>}
                    </td>
                    <td className="px-2 py-2 min-w-[160px]">
                      <select
                        data-testid={`order-cart-supplier-${i}`}
                        value={r.supplier_contact_id}
                        onChange={(e) => updateRow(r._uid, { supplier_contact_id: e.target.value })}
                        className="input py-1 bg-white text-xs"
                        disabled={!r.include}
                      >
                        <option value="">— seç —</option>
                        {suppliers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" min="1" className="input py-1 font-mono-num text-right text-xs"
                             data-testid={`order-cart-qty-${i}`}
                             value={r.quantity}
                             onChange={(e) => updateRow(r._uid, { quantity: e.target.value })} />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" step="0.01" className={`input py-1 font-mono-num text-right text-xs ${
                        r.cost_missing ? "border-amber-500 bg-amber-50" : ""
                      }`}
                             data-testid={`order-cart-unit-${i}`}
                             value={r.unit_cost}
                             title={r.cost_missing ? "Eski kayıt — tedarikçi maliyet bilgisi yok, elle girin" : ""}
                             onChange={(e) => {
                               const v = e.target.value;
                               const nu = Number(v) || 0;
                               const nq = Number(r.quantity) || 0;
                               const m2 = Number(r.total_m2) || 0;
                               // Birim (₺/m²) değişince Toplam'ı m² üzerinden yeniden hesapla
                               updateRow(r._uid, {
                                 unit_cost: v,
                                 total_cost: m2 > 0 ? nu * m2 : nu * nq,
                                 cost_missing: false,
                               });
                             }} />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" step="0.01" className="input py-1 font-mono-num text-right text-xs"
                             data-testid={`order-cart-total-${i}`}
                             value={r.total_cost}
                             onChange={(e) => updateRow(r._uid, { total_cost: e.target.value })} />
                    </td>
                    <td className="px-2 py-2">
                      <input type="date" className="input py-1 text-xs"
                             data-testid={`order-cart-delivery-${i}`}
                             value={r.delivery_date}
                             onChange={(e) => updateRow(r._uid, { delivery_date: e.target.value })} />
                    </td>
                    <td className="px-2 py-2 min-w-[140px]">
                      <input type="text" className="input py-1 text-xs"
                             data-testid={`order-cart-notes-${i}`}
                             placeholder="opsiyonel"
                             value={r.notes}
                             onChange={(e) => updateRow(r._uid, { notes: e.target.value })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t-2 border-black bg-neutral-50">
          <div className="text-xs text-neutral-500 mr-auto">
            {activeRows.length === 0 ? "Hiçbir kalem seçili değil"
             : anyMissingSupplier ? "Bazı kalemlerde tedarikçi seçilmemiş"
             : `${activeRows.length} sipariş açılacak (${supplierCount} tedarikçi)`}
          </div>
          <button onClick={onClose} className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest">İptal</button>
          <button
            onClick={save}
            disabled={saving || activeRows.length === 0 || anyMissingSupplier}
            data-testid="supplier-order-save"
            className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
          >
            <Save size={13} />
            {saving ? "Açılıyor…" : `${activeRows.length} Sipariş Aç`}
          </button>
        </div>

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:6px 8px;font-size:12px;outline:none;background:white}.input:focus{border-color:black}`}</style>
      </div>
    </div>
  );
}
